import { supabase, CRM_API_BASE } from './supabase';

/**
 * Wrapper genérico pra chamadas autenticadas no crm-api (Fastify).
 * Anexa o JWT do Supabase Auth no header Authorization automaticamente.
 */
export async function crmApi<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<{ ok: boolean; data?: T; error?: string; status: number }> {
  if (!CRM_API_BASE) {
    return { ok: false, error: 'CRM_API_BASE not configured', status: 0 };
  }
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);

  try {
    const res = await fetch(`${CRM_API_BASE}${path}`, { ...init, headers });
    const ct = res.headers.get('content-type') ?? '';
    const body: unknown = ct.includes('application/json') ? await res.json() : await res.text();
    if (!res.ok) {
      const msg =
        typeof body === 'string'
          ? body
          : (body as { error?: string; message?: string }).error ??
            (body as { error?: string; message?: string }).message ??
            `HTTP ${res.status}`;
      return { ok: false, error: msg, status: res.status };
    }
    return { ok: true, data: body as T, status: res.status };
  } catch (e) {
    return { ok: false, error: (e as Error).message, status: 0 };
  }
}

export type WhuChannel = 'default' | 'fallback' | 'fb_ig' | 'meta_api';

export const HotLeadApi = {
  transfer(args: {
    attendance_id: string;
    source_event: string;
    specialty?: string;
    patient_id?: number;
    channel?: WhuChannel;
  }) {
    return crmApi<{
      ok: boolean;
      transferred: boolean;
      target?: string;
      decision?: string;
      whu_status?: number;
      fallback?: string;
    }>('/api/hot-lead/transfer', { method: 'POST', body: JSON.stringify(args) });
  },
};

export const LgpdApi = {
  forget(patientId: string | number) {
    return crmApi<{ ok: boolean; patient_id: number; scheduled_purge_in_days: number }>(
      `/api/patient/${patientId}/forget`,
      { method: 'POST' },
    );
  },
};

export interface AttendanceRow {
  id?: string;
  uuid?: string;
  contactId?: string;
  contactName?: string;
  contactPhone?: string;
  status?: string;
  unreadCount?: number;
  lastMessage?: string;
  lastMessageAt?: string;
  assignedTo?: string;
  sectorId?: string;
  [k: string]: unknown;
}

export const AttendanceApi = {
  async list(args: { channel?: WhuChannel; page?: number; limit?: number; status?: string } = {}) {
    const p = new URLSearchParams();
    if (args.channel) p.set('channel', args.channel);
    if (args.page) p.set('page', String(args.page));
    if (args.limit) p.set('limit', String(args.limit));
    if (args.status) p.set('status', args.status);
    return crmApi<{
      ok: boolean;
      channel: WhuChannel;
      page: number;
      limit: number;
      data: { data?: AttendanceRow[]; chats?: AttendanceRow[]; items?: AttendanceRow[] } | AttendanceRow[];
    }>(`/api/attendances?${p.toString()}`);
  },
  finalize(id: string, channel: WhuChannel = 'default') {
    return crmApi<{ ok: boolean; status?: number }>(
      `/api/attendance/${encodeURIComponent(id)}/finalize`,
      { method: 'POST', body: JSON.stringify({ channel }) },
    );
  },
  note(args: {
    id: string;
    channel?: WhuChannel;
    number: string;
    message: string;
    contactId?: string;
  }) {
    return crmApi<{ ok: boolean; status?: number }>(
      `/api/attendance/${encodeURIComponent(args.id)}/note`,
      {
        method: 'POST',
        body: JSON.stringify({
          channel: args.channel ?? 'default',
          number: args.number,
          message: args.message,
          contactId: args.contactId,
        }),
      },
    );
  },
};

export interface RoutingRule {
  id: number;
  name: string;
  source_event: string;
  priority: number;
  target_employee_id: number | null;
  fallback_role: string | null;
  active: boolean;
  condition_sql?: string | null;
  created_at?: string;
  updated_at?: string;
}

export type RoutingRuleInput = Omit<RoutingRule, 'id' | 'created_at' | 'updated_at' | 'condition_sql'>;

export const RoutingRulesApi = {
  list() {
    return crmApi<{ ok: boolean; data: RoutingRule[] }>('/api/admin/routing-rules');
  },
  create(rule: RoutingRuleInput) {
    return crmApi<{ ok: boolean; data: RoutingRule }>('/api/admin/routing-rules', {
      method: 'POST',
      body: JSON.stringify(rule),
    });
  },
  update(id: number, patch: Partial<RoutingRuleInput>) {
    return crmApi<{ ok: boolean; data: RoutingRule }>(`/api/admin/routing-rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  remove(id: number) {
    return crmApi<{ ok: boolean }>(`/api/admin/routing-rules/${id}`, { method: 'DELETE' });
  },
};

export interface EmployeeOption {
  id: number;
  name: string;
  role: string;
  is_active: boolean;
  whu_user_id?: string | null;
  extension_3cx?: string | null;
  last_ping_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export type EmployeeInput = {
  name: string;
  role: 'admin' | 'coordinator' | 'attendant' | 'viewer';
  whu_user_id?: string | null;
  extension_3cx?: string | null;
  is_active?: boolean;
};

export const EmployeesApi = {
  list() {
    return crmApi<{ ok: boolean; data: EmployeeOption[] }>('/api/admin/employees');
  },
  create(emp: EmployeeInput) {
    return crmApi<{ ok: boolean; data: EmployeeOption }>('/api/admin/employees', {
      method: 'POST',
      body: JSON.stringify(emp),
    });
  },
  update(id: number, patch: Partial<EmployeeInput>) {
    return crmApi<{ ok: boolean; data: EmployeeOption }>(`/api/admin/employees/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  deactivate(id: number) {
    return crmApi<{ ok: boolean; data: EmployeeOption }>(`/api/admin/employees/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================================
// Vissmed — import XLS do relatório "Relação de Clientes"
// ============================================================

export interface VissmedPreviewRow {
  vissmed_id: number | null;
  full_name: string;
  phone_e164: string | null;
  cpf: string | null;
  // Exibidos:
  total_spent_cents: number | null;   // LTV total historico (Vissmed)
  total_visits: number | null;        // pra calcular ticket medio (= total / qtd)
  first_visit_at: string | null;
  last_visit_at: string | null;
  last_specialty: string | null;
  // Guardado mas NAO exibido (Fonte do Vissmed nao eh canal de marketing real):
  source_original?: string | null;
}

export interface VissmedImportStats {
  filename: string;
  sizeBytes: number;
  totalRead: number;
  validRows: number;
  issues: number;
  withVissmedId: number;
  withPhone: number;
  withCpf: number;
  withLtv: number;
  totalLtvCents: number;
}

export interface VissmedImportResponse {
  mode: 'dry_run' | 'commit';
  mapping: Record<string, string | null>;
  stats: VissmedImportStats;
  preview: {
    firstRows: VissmedPreviewRow[];
    topLtv: VissmedPreviewRow[];
    issues: Array<{ line: number; reason: string }>;
  };
  result?: {
    created: number;
    updated: number;
    snapshots: number;
    issues: number;
    elapsedMs: number;
  };
}

export interface VissmedLastImport {
  id: number;
  sync_date: string;
  sync_type: string;
  total_changes: number;
  by_type: Record<string, number>;
  sample: Record<string, unknown>;
}

/** Upload especial — multipart/form-data, não JSON. */
async function crmApiMultipart<T = unknown>(
  path: string,
  file: File,
): Promise<{ ok: boolean; data?: T; error?: string; status: number }> {
  if (!CRM_API_BASE) return { ok: false, error: 'CRM_API_BASE not configured', status: 0 };

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;

  const form = new FormData();
  form.append('file', file, file.name);

  const headers = new Headers();
  if (token) headers.set('authorization', `Bearer ${token}`);
  // IMPORTANT: não setar content-type — browser coloca boundary multipart automaticamente.

  try {
    const res = await fetch(`${CRM_API_BASE}${path}`, { method: 'POST', headers, body: form });
    const ct = res.headers.get('content-type') ?? '';
    const body: unknown = ct.includes('application/json') ? await res.json() : await res.text();
    if (!res.ok) {
      const msg =
        typeof body === 'string'
          ? body
          : (body as { error?: string; message?: string }).error ??
            (body as { error?: string; message?: string }).message ??
            `HTTP ${res.status}`;
      return { ok: false, error: msg, status: res.status };
    }
    return { ok: true, data: body as T, status: res.status };
  } catch (e) {
    return { ok: false, error: (e as Error).message, status: 0 };
  }
}

export const VissmedApi = {
  /** dryRun (default) — analisa o arquivo e devolve preview sem tocar no DB. */
  preview(file: File) {
    return crmApiMultipart<VissmedImportResponse>('/api/admin/vissmed/import', file);
  },
  /** commit — faz o UPSERT de verdade. */
  commit(file: File) {
    return crmApiMultipart<VissmedImportResponse>('/api/admin/vissmed/import?commit=true', file);
  },
  lastImports() {
    return crmApi<{ imports: VissmedLastImport[] }>('/api/admin/vissmed/last-imports');
  },
};
